import {
  type GraphGroup,
  type GraphListResult,
  type GraphServicePrincipal,
  listGroups,
  type ListGroupsOptions,
  listServicePrincipals,
  type ListServicePrincipalsOptions,
} from '@ns/plugin-msgraph-proxy-client';
import {
  QueryClient,
  type QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import { type GraphTransport, useGraphTransport } from './transport';

type TransportKeys = keyof GraphTransport | 'signal';

/** What a groups request asks Graph, without how it travels. */
export type GroupsQuery = Omit<ListGroupsOptions, TransportKeys>;

/** What a service-principals request asks Graph, without how it travels. */
export type ServicePrincipalsQuery = Omit<
  ListServicePrincipalsOptions,
  TransportKeys
>;

/**
 * The TanStack identity of one groups query.
 *
 * @remarks
 * It holds the query, never the transport. TanStack hashes it as JSON, so a
 * function inside the query, such as `resilience.retry.shouldRetry`, is not
 * part of the identity.
 */
export type GroupsQueryKey = readonly ['msgraph-proxy', 'groups', GroupsQuery];

/** The TanStack identity of one service-principals query, with the same limits as {@link GroupsQueryKey}. */
export type ServicePrincipalsQueryKey = readonly [
  'msgraph-proxy',
  'service-principals',
  ServicePrincipalsQuery,
];

/** The shared cache for Graph reads in pickers and submit validation. */
export const graphQueryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: false, staleTime: 60_000 },
  },
});

/**
 * Builds the TanStack key and request for one groups query.
 *
 * @param query - What to ask Graph.
 * @param transport - How the request travels, from {@link readGraphTransport} or {@link useGraphTransport}.
 * @returns The key, which holds only the query, and the request function.
 */
export function buildGroupsQueryOptions(
  query: GroupsQuery,
  transport: GraphTransport,
) {
  const queryKey: GroupsQueryKey = ['msgraph-proxy', 'groups', query];
  return {
    queryFn: ({ signal }: QueryFunctionContext<GroupsQueryKey>) =>
      listGroups({ ...query, ...transport, signal }),
    queryKey,
  };
}

/**
 * Builds the TanStack key and request for one service-principals query.
 *
 * @param query - What to ask Graph.
 * @param transport - How the request travels, from {@link readGraphTransport} or {@link useGraphTransport}.
 * @returns The key, which holds only the query, and the request function.
 */
export function buildServicePrincipalsQueryOptions(
  query: ServicePrincipalsQuery,
  transport: GraphTransport,
) {
  const queryKey: ServicePrincipalsQueryKey = [
    'msgraph-proxy',
    'service-principals',
    query,
  ];
  return {
    queryFn: ({ signal }: QueryFunctionContext<ServicePrincipalsQueryKey>) =>
      listServicePrincipals({ ...query, ...transport, signal }),
    queryKey,
  };
}

/**
 * Lists groups as a TanStack query, with the transport taken from Backstage's APIs.
 *
 * @typeParam TSelected - What `options.select` turns the page into.
 * @param query - What to ask Graph.
 * @param options - Every TanStack option except `queryFn` and `queryKey`.
 * @returns The TanStack query result, cached in {@link graphQueryClient}.
 */
export function useGroups<TSelected = GraphListResult<GraphGroup>>(
  query: GroupsQuery,
  options?: Omit<
    UseQueryOptions<
      GraphListResult<GraphGroup>,
      Error,
      TSelected,
      GroupsQueryKey
    >,
    'queryFn' | 'queryKey'
  >,
): UseQueryResult<TSelected, Error> {
  const recipe = buildGroupsQueryOptions(query, useGraphTransport());
  return useQuery({ ...options, ...recipe }, graphQueryClient);
}

/**
 * Lists service principals as a TanStack query, with the transport taken from Backstage's APIs.
 *
 * @typeParam TSelected - What `options.select` turns the page into.
 * @param query - What to ask Graph.
 * @param options - Every TanStack option except `queryFn` and `queryKey`.
 * @returns The TanStack query result, cached in {@link graphQueryClient}.
 */
export function useServicePrincipals<
  TSelected = GraphListResult<GraphServicePrincipal>,
>(
  query: ServicePrincipalsQuery,
  options?: Omit<
    UseQueryOptions<
      GraphListResult<GraphServicePrincipal>,
      Error,
      TSelected,
      ServicePrincipalsQueryKey
    >,
    'queryFn' | 'queryKey'
  >,
): UseQueryResult<TSelected, Error> {
  const recipe = buildServicePrincipalsQueryOptions(query, useGraphTransport());
  return useQuery({ ...options, ...recipe }, graphQueryClient);
}
